import { Link, useLocation } from "wouter"
import { ArrowLeft } from "lucide-react"
import logo from "@/assets/logo-cineblocks-1.svg"
import { useAuthContext } from "@/context/authentication/auth-context"
import { StepNavigation } from "../components/person-steps/step-navigation"
import { usePersonAdd } from "../hooks/use-person-add"
import { PersonPFAddView } from "./person-pf-add-view"
import { PersonPJAddView } from "./person-pj-add-view"
import { PersonAttachmentsView } from "./person-attachments-view"
import { PersonPersonalizationView } from "./person-personalization-view"
import { clearCompletedSteps, clearFilePreviews } from "../utils/upload-helpers"
import { ONBOARDING_STEP_TITLES } from "../constants/onboarding-steps"
import { useUpdateOnboardingSteps } from "@/services/queries/useUsers"

export function PersonAddView() {
  const [, navigate] = useLocation()
  const { person } = useAuthContext()
  const personId = person?.id || 0
  const userId = person?.user?.id || 0

  const { mutateAsync: updateOnboardingSteps } = useUpdateOnboardingSteps();

  const {
    currentStep,
    setCurrentStep,
    navigateToStep,
    completedSteps,
    markStepComplete,
    getStepStatus,
  } = usePersonAdd(personId)

  const stepInfo = [
    {
      title: ONBOARDING_STEP_TITLES[0],
      description: "Preencha seus dados pessoais para o cadastro na plataforma.",
      component: (
        <PersonPFAddView
          onContinue={() => {
            markStepComplete(1)
            setCurrentStep(2)
          }}
          onBack={() => setCurrentStep(1)}
        />
      ),
    },
    {
      title: ONBOARDING_STEP_TITLES[1],
      description: "Preencha os dados da empresa para o cadastro na plataforma.",
      component: (
        <PersonPJAddView
          onContinue={() => {
            markStepComplete(2)
            setCurrentStep(3)
            updateOnboardingSteps({ id_user: userId, step: '5' })
          }}
          onBack={() => setCurrentStep(1)}
        />
      ),
    },
    {
      title: ONBOARDING_STEP_TITLES[2],
      description: "Envie os documentos necessários para validação.",
      component: (
        <PersonAttachmentsView
          personId={personId}
          onContinue={() => {
            markStepComplete(3)
            setCurrentStep(4)
            updateOnboardingSteps({ id_user: userId, step: '6' })
          }}
          onBack={() => setCurrentStep(2)}
        />
      ),
    },
    {
      title: ONBOARDING_STEP_TITLES[3],
      description: "Personalize seu perfil e adicione suas redes sociais.",
      component: (
        <PersonPersonalizationView
          onContinue={async () => {
            console.log("Starting onContinue...")
            markStepComplete(4)
            clearFilePreviews(personId)
            clearCompletedSteps(personId)
            console.log("Updating onboarding steps...")
            try {
              await updateOnboardingSteps({ id_user: userId, step: '7' })
              console.log("Onboarding steps updated. Navigating...")
              navigate("/cadastrar-projeto")
            } catch (e) {
              console.error("Error updating onboarding steps:", e)
            }
          }}
          onBack={() => setCurrentStep(3)}
        />
      ),
    },
  ]

  return (
    <div className="h-screen lg:max-w-screen-2xl mx-auto">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-start mb-8">
          <Link
            href="/onboarding"
            className="w-2/5 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </Link>

          <div className="flex items-center gap-2">
            <img src={logo} alt="Logo Cineblocks" className="w-6 h-6" />
            <span className="text-2xl font-bold">cineblocks</span>
          </div>
        </div>

        <StepNavigation
          totalSteps={4}
          onStepChange={navigateToStep}
          getStepStatus={getStepStatus}
        />

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            {stepInfo[currentStep - 1].title}
          </h2>
          <p className="text-gray-400">
            {stepInfo[currentStep - 1].description}
          </p>
        </div>

        {stepInfo[currentStep - 1].component}
      </div>
    </div>
  )
}
